import { Link, Text } from "@oysterhr/ui"
import { useFlags } from "@oysterhr/feature-flags"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { usePearlContext } from "@oysterhr/pearl"
import type { HiringFTEPearlContext } from "~/shared/components/PearlTooltip/types"
import {
  CountryIsoCode,
  EngagementSeniorityLevelType,
  ProvidersEnum,
  TrustLevelEnum,
} from "@oysterhr/schema"
import { ProviderInsight } from "./ProviderInsight"
import { useParams } from "react-router-dom"
import {
  buildSalaryInsightsSearchParams,
  SalaryInsightsQueryParams,
  companySalaryInsightsResultsPath,
} from "@oysterhr/compensation-tools"
import { useDisplayHourlyRateSalary } from "@oysterhr/hiring/full-time-employee"
import { useSalaryBenchmarksQuery } from "./hooks/useSalaryBenchmarks"

export const SalaryInsight = () => {
  const { t } = useTranslation("knowledge_base/SalaryInsight")
  const { cctHideDataFromFiguresOnTheHiringFlow } = useFlags()
  const { pearlExtraInputs: extraInputs } = usePearlContext<HiringFTEPearlContext>()
  const displayHourlyRateSalary = useDisplayHourlyRateSalary(extraInputs?.countryCode as string)
  const { companyStrongId } = useParams()
  const providerIds: ProvidersEnum[] = useMemo(
    () => [ProvidersEnum.FiguresApi, ProvidersEnum.OysterPolicy].filter(Boolean),
    [],
  )

  const { data } = useSalaryBenchmarksQuery({
    variables: {
      countryCode: extraInputs?.countryCode as CountryIsoCode,
      roleTypeId: extraInputs?.jobExternalId as string,
      seniorityLevel: extraInputs?.jobLevelCode as EngagementSeniorityLevelType,
      currencyCode: extraInputs?.currencyCode as string,
      providerIds,
    },
    skip: !extraInputs?.jobExternalId,
    showErrorModal: false,
  })

  const benchmarks = data?.compensationInsights?.salaryBenchmarks

  const acceptedTrustLevels = [TrustLevelEnum.Excellent, TrustLevelEnum.Good, TrustLevelEnum.Fair]

  const figuresBenchmark = cctHideDataFromFiguresOnTheHiringFlow
    ? null
    : benchmarks?.find(({ providerId }) => providerId === ProvidersEnum.FiguresApi)
  const oysterBenchmark = benchmarks?.find(
    ({ providerId }) => providerId === ProvidersEnum.OysterPolicy,
  )

  const searchParams = buildSalaryInsightsSearchParams({
    role: {
      id: (extraInputs?.jobExternalId as string) ?? "",
      name: (extraInputs?.jobRole as string) ?? "",
    },
    country: (extraInputs?.countryCode as CountryIsoCode) ?? "",
    level: (extraInputs?.jobLevelCode as EngagementSeniorityLevelType) ?? "",
    currency: (extraInputs?.currencyCode as string) ?? "",
    amount: (extraInputs?.amount as string) ?? "",
  })

  const salaryInsightsLink = companySalaryInsightsResultsPath({
    companyId: companyStrongId,
    query: Object.fromEntries(searchParams) as SalaryInsightsQueryParams,
  })

  return (
    <div>
      <Text>{displayHourlyRateSalary ? t("header.hourlyRate") : t("header.default")}</Text>
      {benchmarks && benchmarks.length > 0 && (
        <div className="mt-6">
          {figuresBenchmark &&
            acceptedTrustLevels.includes(figuresBenchmark.trustLevel as TrustLevelEnum) && (
              <ProviderInsight benchmark={figuresBenchmark} />
            )}
          {oysterBenchmark && <ProviderInsight benchmark={oysterBenchmark} />}
          <Text className="mt-5">
            {t("findOutMore")}&nbsp;
            <Link to={salaryInsightsLink} target="_blank" className="inline font-normal">
              {t("salaryInsightsTool")}
            </Link>
          </Text>
        </div>
      )}
    </div>
  )
}
